import { baseCleanProps } from "common/api/Api";
import { fetcher } from "common/api/Fetcher";
import { downloadFile } from "common/helpers/File";
import { DocumentRecord, IDocumentData, toFormData } from "common/records/Document";
import { IMetaPaginationData } from "common/records/MetaPagination";
import { IRecipientRecord } from "common/records/Recipient";
import { ISignedDocumentData, SignedDocumentRecord } from "common/records/SignedDocument";
import { SignedDocumentParamsRecord } from "common/records/index/SignedDocumentParams";
import { toParams } from "common/records/index/QueryParamsBase";
export interface IValidateSignedDocumentResponse {
  document: Partial<IDocumentData>;
  signature_validations: string;
}

export interface ISignedDocumentResponse {
  signed_document: ISignedDocumentData;
}

export interface ISignedDocumentIndexResponse {
  signed_documents: ISignedDocumentData[];
  meta: { pagination: IMetaPaginationData };
}

const url = "api/v1/signed_documents/";

export const list = (queryParams: SignedDocumentParamsRecord): Promise<ISignedDocumentIndexResponse> => {
  const parameters = toParams(queryParams);
  const include = parameters.include;
  delete parameters.include;

  return fetcher.get<ISignedDocumentIndexResponse>("api/v1/signed_documents", { ...parameters, "include[]": include });
};

export const sign = async (signatureId: string): Promise<any> => {
  const actionUrl = fetcher.joinUrls(url, "sign");
  return fetcher.get(actionUrl, { key: signatureId });
};

export const loadSignedDocument = (signedDocId: number): Promise<{ signed_document: ISignedDocumentData }> => {
  const actionUrl = url.concat(signedDocId.toString());
  return fetcher.get(actionUrl, { "include[]": ["document", "recipients"] });
};

export const notify = (documentId: number, recipient?: number): Promise<void> => {
  const actionUrl = fetcher.joinUrls(url.concat(documentId.toString()), "notify");

  if (recipient) return fetcher.post(actionUrl.toString(), { "recipients[]": recipient.toString() });
  else return fetcher.post(actionUrl.toString());
};

export const validate = (document: DocumentRecord, isModified: boolean): Promise<IValidateSignedDocumentResponse> => {
  if (isModified) {
    const actionUrl = url.concat("validate");
    return fetcher.post(actionUrl.toString(), toFormData(document), { formData: true });
  } else {
    const actionUrl = url.concat("validate");
    return fetcher.post(actionUrl.toString(), { document_id: document.id });
  }
};

export const create = (
  document: DocumentRecord,
  signedDocument: SignedDocumentRecord,
  options: {
    notify: boolean;
    message: string;
  },
): Promise<ISignedDocumentResponse> => {
  const params = {
    document: baseCleanProps(document.toJS() as IDocumentData),
    signed_document: cleanProps(signedDocument),
    include: ["document", "recipients"],
    ...options,
  };
  return fetcher.post(url, params);
};

export const cleanProps = (signedDocument: SignedDocumentRecord): ISignedDocumentData => {
  let signedDocumentParams: any = signedDocument.toJS();
  signedDocumentParams = baseCleanProps(signedDocumentParams);
  signedDocumentParams.recipients_attributes = signedDocumentParams.recipients.filter(
    (rec: IRecipientRecord) => (rec.required === true && rec.included === true) || rec.included === true,
  );
  delete signedDocumentParams.recipients;
  delete signedDocumentParams.validation_status;
  // Index-row-only fields — never part of a create/update payload (`kind` is server-derived).
  delete signedDocumentParams.estimate;
  delete signedDocumentParams.job;
  delete signedDocumentParams.kind;

  if (!signedDocumentParams.estimate_id) delete signedDocumentParams.estimate_id;

  return signedDocumentParams as any as ISignedDocumentData;
};

export const getHistoryReport = async (signedDocumentId: number, kind: "standard" | "detailed"): Promise<void> => {
  const actionUrl = fetcher.joinUrls(url, `${signedDocumentId.toString()}/history_report.pdf`);
  await downloadFile(actionUrl.concat("?kind=", kind), `history_report_${kind}.pdf`);
};

export const voidDocument = (signedDocumentId: number, reason: string | undefined): Promise<void> => {
  const actionUrl = fetcher.joinUrls(url, `${signedDocumentId}/void`);
  return fetcher.post(actionUrl, { reason });
};
